import React, { useState, useEffect, useMemo } from 'react'
import { FaPlus, FaArrowLeft, FaArrowRight, FaChevronDown, FaDownload, FaSort, FaSortUp, FaSortDown, FaTrash, FaCheck, FaEye, FaEyeSlash } from 'react-icons/fa'
import { Link } from 'react-router'
import { Box, Wrap, WrapItem, Stack, Icon, Text, Button, Input, Checkbox, Menu, MenuItem, MenuGroup, MenuSeparator, Progress, CircularProgress, Loading, Badge, Tag, ColouredTag, SimpleTable, Tooltip, EmptyBox, ConfirmButton, SimplePopover, SimpleModal } from '@/components/lib'
import api from '../../../api'
import utils from '../../../utils/utils'
import useStore from '../../../store'

import ParticipantCreator from '../../includes/ParticipantCreator'
import ParticipantJoinInfo from '../../includes/ParticipantJoinInfo'
import { InsightCarousel } from '../../includes/ParticipantInsightCards'
import SavedFilters from './SavedFilters'

const SCHEDULE_FILTER_LABELS = { behind: 'Behind schedule', on: 'On schedule' }
const ALLOCATION_FILTER_LABELS = { unallocated: 'Unallocated', allocated: 'Allocated' }
const JOIN_FILTER_LABELS = { notJoined: 'Not joined', joined: 'Joined' }

export default function ParticipantsList () {
  const [participants, setParticipants] = useState([])
  const [selectionParticipants, setSelectionParticipants] = useState([])
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [total, setTotal] = useState(0)
  const [nextPageAvailable, setNextPageAvailable] = useState(false)
  const [prevPageAvailable, setPrevPageAvailable] = useState(false)

  const { user, trial, team, updateTrial, groups, sites, participantsTableFilters, setParticipantsTableFilters, getTrialUsers } = useStore()
  const trialUsers = useMemo(() => getTrialUsers(trial?._id), [trial?._id, getTrialUsers])
  const insightsFeature = team && utils.hasFeature(team, 'participantInsights') && trial?.participantInsightsEnabled && utils.canReadTrialInsights(user, trial)

  const {
    page = 1,
    sort = 'id',
    sortOrder = 'asc',
    searchTerm = '',
    selectedSiteId = null,
    groupIncludeIds = [],
    groupExcludeIds = [],
    groupNoneFilter = null,
    scheduleFilters = [],
    allocationFilters = [],
    joinFilters = [],
    showArchived = false
  } = participantsTableFilters || {}

  useEffect(
    fetchParticipants,
    [
      trial && trial._id,
      page,
      searchTerm,
      groupIncludeIds,
      groupExcludeIds,
      groupNoneFilter,
      selectedSiteId,
      sort,
      sortOrder,
      scheduleFilters,
      allocationFilters,
      joinFilters,
      showArchived
    ]
  )

  const hasAnyFiltersApplied =
    (scheduleFilters?.length || 0) > 0 ||
    (allocationFilters?.length || 0) > 0 ||
    (joinFilters?.length || 0) > 0 ||
    !!showArchived ||
    !!selectedSiteId ||
    (groupIncludeIds?.length || 0) > 0 ||
    (groupExcludeIds?.length || 0) > 0 ||
    !!groupNoneFilter

  const appliedFilterCount =
    (scheduleFilters?.length || 0) +
    (allocationFilters?.length || 0) +
    (joinFilters?.length || 0) +
    (showArchived ? 1 : 0) +
    (selectedSiteId ? 1 : 0) +
    (groupIncludeIds?.length || 0) +
    (groupExcludeIds?.length || 0) +
    (groupNoneFilter ? 1 : 0)

  function groupCycleMode (groupId) {
    const gid = String(groupId)
    if (groupIncludeIds.some(id => String(id) === gid)) return 'include'
    if (groupExcludeIds.some(id => String(id) === gid)) return 'exclude'
    return 'neutral'
  }

  function cycleGroupFilter (groupId) {
    const gid = String(groupId)
    let inc = [...(groupIncludeIds || [])]
    let exc = [...(groupExcludeIds || [])]
    if (inc.some(id => String(id) === gid)) {
      inc = inc.filter(id => String(id) !== gid)
      exc = [...exc, groupId]
    } else if (exc.some(id => String(id) === gid)) {
      exc = exc.filter(id => String(id) !== gid)
    } else {
      inc = [...inc, groupId]
    }
    setParticipantsTableFilters({ groupIncludeIds: inc, groupExcludeIds: exc, page: 1 })
  }

  function cycleNoGroupFilter () {
    let next = null
    if (groupNoneFilter == null) next = 'include'
    else if (groupNoneFilter === 'include') next = 'exclude'
    else next = null
    setParticipantsTableFilters({ groupNoneFilter: next, page: 1 })
  }

  function toggleFilterOption (key, value) {
    const current = (participantsTableFilters?.[key] || [])
    const next = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value]
    setParticipantsTableFilters({ [key]: next, page: 1 })
  }

  function fetchParticipants () {
    if (!trial?._id) return
    setLoading(true)
    const filters = {
      showArchived,
      scheduleBehind: scheduleFilters.includes('behind'),
      scheduleOnSchedule: scheduleFilters.includes('on'),
      allocationUnallocated: allocationFilters.includes('unallocated'),
      allocationAllocated: allocationFilters.includes('allocated'),
      joinedJoined: joinFilters.includes('joined'),
      joinedNotJoined: joinFilters.includes('notJoined'),
      groupIncludeIds: groupIncludeIds || [],
      groupExcludeIds: groupExcludeIds || [],
      groupIncludeNone: groupNoneFilter === 'include',
      groupExcludeNone: groupNoneFilter === 'exclude'
    }
    api.trials.getParticipants(
      trial._id,
      page,
      searchTerm,
      selectedSiteId,
      false,
      sort,
      sortOrder,
      filters,
      ({ participants, nextPageAvailable, prevPageAvailable, total }) => {
        setLoading(false)
        setParticipants(participants)
        setTotal(total)

        // Keep the global trial participant count in sync, but only when
        // we're looking at the "all participants" view (no filters/search/group/site).
        const isUnfiltered =
          !searchTerm &&
          !selectedSiteId &&
          (groupIncludeIds?.length || 0) === 0 &&
          (groupExcludeIds?.length || 0) === 0 &&
          !groupNoneFilter &&
          (scheduleFilters?.length || 0) === 0 &&
          (allocationFilters?.length || 0) === 0 &&
          (joinFilters?.length || 0) === 0 &&
          !showArchived
        if (isUnfiltered && (trial?.participantCount !== total)) {
          updateTrial(trial._id, { participantCount: total })
        }

        setNextPageAvailable(nextPageAvailable)
        setPrevPageAvailable(prevPageAvailable)
      },
      err => {
        setLoading(false)
        utils.toast('error', 'Unable to fetch participants', err.message)
      }
    )
  }

  function addToGroup (id, groupId) {
    api.groups.addMember(groupId, id, ({ groups }) => {
      const newParticipants = Object.assign([], participants).map(p => {
        if (p._id === id) p.groups = groups
        return p
      })
      setParticipants(newParticipants)
    }, err => utils.toast('error', 'Unable to add participant to group', err.message))
  }
  function removeFromGroup (id, groupId) {
    api.groups.removeMember(groupId, id, ({ groups }) => {
      const newParticipants = Object.assign([], participants).map(p => {
        if (p._id === id) p.groups = groups
        return p
      })
      setParticipants(newParticipants)
    }, err => utils.toast('error', 'Unable to remove participant from group', err.message))
  }

  function addToSite (id, siteId) {
    api.sites.addMember(siteId, id, ({ sites }) => {
      const newParticipants = Object.assign([], participants).map(p => {
        if (p._id === id) p.sites = sites
        return p
      })
      setParticipants(newParticipants)
    }, err => utils.toast('error', 'Unable to add participant to site', err.message))
  }
  function removeFromSite (id, siteId) {
    api.sites.removeMember(siteId, id, ({ sites }) => {
      const newParticipants = Object.assign([], participants).map(p => {
        if (p._id === id) p.sites = sites
        return p
      })
      setParticipants(newParticipants)
    }, err => utils.toast('error', 'Unable to remove participant from site', err.message))
  }

  function updateParticipant (id, data) {
    api.trials.updateParticipant(trial._id, id, data, (participant) => {
      utils.toast('success', 'Participant updated')
      const newParticipants = Object.assign([], participants).map(p => {
        if (p._id === id) return { ...p, ...participant }
        return p
      })
      setParticipants(newParticipants)
    }, err => utils.toast('error', 'Unable to update participant', err.message))
  }

  function deleteParticipant (id) {
    api.trials.deleteParticipant(trial._id, id, () => {
      utils.toast('success', 'Participant removed')
      const newParticipants = Object.assign([], participants).filter(p => p._id !== id)
      setParticipants(newParticipants)
      setSelectionParticipants([])
      updateTrial(trial._id, { participantCount: trial.participantCount - 1 })
    }, err => utils.toast('error', 'Unable to delete participant', err.message))
  }

  function bulkAssignGroup (groupId) {
    const updates = selectionParticipants.map(id => {
      const p = participants.find(p => p._id === id)
      const currentGroups = (p?.groups || []).map(String)
      const merged = currentGroups.includes(String(groupId)) ? currentGroups : [...currentGroups, String(groupId)]
      return { participant: id, groups: merged }
    })
    api.trials.bulkUpdateParticipants(trial._id, updates, () => {
      utils.toast('success', 'Participants added to group')
      setParticipants(prev => prev.map(p => {
        const upd = updates.find(u => u.participant === p._id)
        return upd ? { ...p, groups: upd.groups } : p
      }))
    }, err => utils.toast('error', 'Unable to assign group', err.message))
  }

  function bulkAssignSite (siteId) {
    const updates = selectionParticipants.map(id => {
      const p = participants.find(p => p._id === id)
      const currentSites = (p?.sites || []).map(String)
      const merged = currentSites.includes(String(siteId)) ? currentSites : [...currentSites, String(siteId)]
      return { participant: id, sites: merged }
    })
    api.trials.bulkUpdateParticipants(trial._id, updates, () => {
      utils.toast('success', 'Participants added to site')
      setParticipants(prev => prev.map(p => {
        const upd = updates.find(u => u.participant === p._id)
        return upd ? { ...p, sites: upd.sites } : p
      }))
    }, err => utils.toast('error', 'Unable to assign site', err.message))
  }

  function bulkDeleteParticipants () {
    const really = window.confirm('Really delete all of the selected participants?')
    if (!really) return
    const ids = selectionParticipants
    if (!ids || !ids.length) window.alert('No participants selected')
    api.trials.deleteParticipants(trial._id, { participants: ids }, () => {
      utils.toast('success', 'Participants removed')
      const newParticipants = Object.assign([], participants).filter(p => ids.indexOf(p._id) === -1)
      setParticipants(newParticipants)
      setSelectionParticipants([])
      updateTrial(trial._id, { participantCount: trial.participantCount - ids.length })
    }, err => utils.toast('error', 'Unable to delete participants', err.message))
  }
  function bulkNotifyParticipants () {
    const ids = selectionParticipants
    if (!ids || !ids.length) window.alert('No participants selected')
    const message = window.prompt(`Write a message to send to these ${ids.length} participants' devices.`)
    if (message) {
      api.trials.createParticipantNotifications(trial._id, { message, participants: ids }, () => {
        utils.toast('success', 'Notifications sent')
      }, err => utils.toast('error', 'Unable to notify participants', err.message))
    };
  }

  function exportCSV () {
    setExporting(true)
    api.trials.getParticipantsCsv(trial._id, { includeArchived: !!showArchived }, ({ csv }) => {
      utils.downloadFile(`data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`, `${trial.name.replace(/ /g, '_')}_participants.csv`)
      utils.toast('success', 'CSV file generated successfully', 'It should now be in your downloads folder.')
      setExporting(false)
    }, err => {
      utils.toast('error', err.message)
      setExporting(false)
    })
  }

  function toggleSelected (id) {
    const newSelectionParticipants = Object.assign([], selectionParticipants)
    const index = newSelectionParticipants.indexOf(id)
    if (index === -1) newSelectionParticipants.push(id)
    if (index > -1) newSelectionParticipants.splice(index, 1)
    setSelectionParticipants(newSelectionParticipants)
  }

  participants.forEach(p => {
    p.groupObjects = groups?.filter(g => p.groups && p.groups.indexOf(g._id) > -1)
    p.siteObjects = sites?.filter(s => p?.sites?.indexOf(s._id) > -1)
  })
  const filteredParticipants = participants

  const filterChips = useMemo(() => {
    const chips = []
    const term = (searchTerm || '').trim()
    if (term) {
      const short = term.length > 36 ? `${term.slice(0, 36)}…` : term
      chips.push({
        key: 'search',
        label: `Search: ${short}`,
        onRemove: () => setParticipantsTableFilters({ searchTerm: '', page: 1 })
      })
    }
    scheduleFilters.forEach(v => {
      const label = SCHEDULE_FILTER_LABELS[v]
      if (!label) return
      chips.push({
        key: `schedule-${v}`,
        label,
        onRemove: () =>
          setParticipantsTableFilters({
            scheduleFilters: scheduleFilters.filter(x => x !== v),
            page: 1
          })
      })
    })
    allocationFilters.forEach(v => {
      const label = ALLOCATION_FILTER_LABELS[v]
      if (!label) return
      chips.push({
        key: `allocation-${v}`,
        label,
        onRemove: () =>
          setParticipantsTableFilters({
            allocationFilters: allocationFilters.filter(x => x !== v),
            page: 1
          })
      })
    })
    joinFilters.forEach(v => {
      const label = JOIN_FILTER_LABELS[v]
      if (!label) return
      chips.push({
        key: `join-${v}`,
        label,
        onRemove: () =>
          setParticipantsTableFilters({
            joinFilters: joinFilters.filter(x => x !== v),
            page: 1
          })
      })
    })
    if (showArchived) {
      chips.push({
        key: 'archived',
        label: 'Archived: shown',
        onRemove: () => setParticipantsTableFilters({ showArchived: false, page: 1 })
      })
    }
    if (selectedSiteId) {
      const site = sites?.find(s => String(s._id) === String(selectedSiteId))
      chips.push({
        key: 'site',
        label: site ? `Site: ${site.name}` : 'Site filter',
        onRemove: () => setParticipantsTableFilters({ selectedSiteId: null, page: 1 })
      })
    }
    if (groupNoneFilter === 'include') {
      chips.push({
        key: 'group-none-include',
        label: 'Ungrouped only',
        onRemove: () => setParticipantsTableFilters({ groupNoneFilter: null, page: 1 })
      })
    }
    if (groupNoneFilter === 'exclude') {
      chips.push({
        key: 'group-none-exclude',
        label: 'In a group',
        onRemove: () => setParticipantsTableFilters({ groupNoneFilter: null, page: 1 })
      })
    }
    groupIncludeIds.forEach(gid => {
      const g = groups?.find(x => String(x._id) === String(gid))
      chips.push({
        key: `g-in-${gid}`,
        label: `In group: ${g?.name || '…'}`,
        onRemove: () =>
          setParticipantsTableFilters({
            groupIncludeIds: groupIncludeIds.filter(id => String(id) !== String(gid)),
            page: 1
          })
      })
    })
    groupExcludeIds.forEach(gid => {
      const g = groups?.find(x => String(x._id) === String(gid))
      chips.push({
        key: `g-ex-${gid}`,
        label: `Not in group: ${g?.name || '…'}`,
        onRemove: () =>
          setParticipantsTableFilters({
            groupExcludeIds: groupExcludeIds.filter(id => String(id) !== String(gid)),
            page: 1
          })
      })
    })
    return chips
  }, [
    searchTerm,
    scheduleFilters,
    allocationFilters,
    joinFilters,
    showArchived,
    selectedSiteId,
    groupIncludeIds,
    groupExcludeIds,
    groupNoneFilter,
    groups,
    sites,
    setParticipantsTableFilters
  ])

  return (
    <Stack gap={3}>
      <Stack direction={{ base: 'column', md: 'row' }} justifyContent='space-between' alignItems={{ base: 'flex-start', md: 'center' }}>
        <Stack direction='row' alignItems='center'>
          <Input
            width={180}
            size='xs'
            value={searchTerm}
            onChange={e => setParticipantsTableFilters({ searchTerm: e.target.value, page: 1 })}
            placeholder='Type to search...'
          />

          <SimplePopover
            closeOnBlur
            contentProps={{ w: { base: '95vw', md: 500 }, maxW: '95vw' }}
            trigger={
              <Button size='xs' variant='outline'>
                {hasAnyFiltersApplied ? `Filters applied (${appliedFilterCount})` : 'Filter'} <Icon as={FaChevronDown} />
              </Button>
            }
            header='Filters'
            body={
              <Stack gap={2}>
                <Stack direction={{ base: 'column', md: 'row' }} gap={2} alignItems='start'>
                  <Box minW='150px'>
                    <Stack gap={1} p={2} borderWidth='1px' borderColor='border.default' borderRadius='md'>
                      <Button
                        size='xs'
                        variant='ghost'
                        justifyContent='space-between'
                        onClick={() => toggleFilterOption('scheduleFilters', 'behind')}
                      >
                        <span>Behind schedule</span>
                        {scheduleFilters.includes('behind') && <Icon as={FaCheck} />}
                      </Button>
                      <Button
                        size='xs'
                        variant='ghost'
                        justifyContent='space-between'
                        onClick={() => toggleFilterOption('scheduleFilters', 'on')}
                      >
                        <span>On schedule</span>
                        {scheduleFilters.includes('on') && <Icon as={FaCheck} />}
                      </Button>

                      <hr />

                      <Button
                        size='xs'
                        variant='ghost'
                        justifyContent='space-between'
                        onClick={() => toggleFilterOption('allocationFilters', 'unallocated')}
                      >
                        <span>Unallocated</span>
                        {allocationFilters.includes('unallocated') && <Icon as={FaCheck} />}
                      </Button>
                      <Button
                        size='xs'
                        variant='ghost'
                        justifyContent='space-between'
                        onClick={() => toggleFilterOption('allocationFilters', 'allocated')}
                      >
                        <span>Allocated</span>
                        {allocationFilters.includes('allocated') && <Icon as={FaCheck} />}
                      </Button>

                      <hr />

                      <Button
                        size='xs'
                        variant='ghost'
                        justifyContent='space-between'
                        onClick={() => toggleFilterOption('joinFilters', 'notJoined')}
                      >
                        <span>Not joined</span>
                        {joinFilters.includes('notJoined') && <Icon as={FaCheck} />}
                      </Button>
                      <Button
                        size='xs'
                        variant='ghost'
                        justifyContent='space-between'
                        onClick={() => toggleFilterOption('joinFilters', 'joined')}
                      >
                        <span>Joined</span>
                        {joinFilters.includes('joined') && <Icon as={FaCheck} />}
                      </Button>

                      <hr />

                      <Button
                        size='xs'
                        variant='ghost'
                        justifyContent='space-between'
                        onClick={() => setParticipantsTableFilters({ showArchived: !showArchived, page: 1 })}
                      >
                        <span>Show archived</span>
                        {showArchived && <Icon as={FaCheck} />}
                      </Button>
                    </Stack>
                  </Box>

                  {sites?.length > 0 && (
                    <Box minW='150px' p={2} borderWidth='1px' borderColor='border.default' borderRadius='md'>
                      <Stack gap={1} maxH='280px' overflowY='auto'>
                        <Button
                          size='xs'
                          variant='ghost'
                          justifyContent='space-between'
                          onClick={() => setParticipantsTableFilters({ selectedSiteId: null, page: 1 })}
                        >
                          <span>All sites</span>
                          {!selectedSiteId && <Icon as={FaCheck} />}
                        </Button>
                        {sites.map(site => (
                          <Button
                            key={site._id}
                            size='xs'
                            variant='ghost'
                            justifyContent='space-between'
                            onClick={() => setParticipantsTableFilters({ selectedSiteId: site._id, page: 1 })}
                          >
                            <Tag size='sm' variant='subtle' label={site.name} />
                            {String(selectedSiteId) === String(site._id) && <Icon as={FaCheck} />}
                          </Button>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  <Box minW='160px' p={2} borderWidth='1px' borderColor='border.default' borderRadius='md'>
                    <Stack gap={0} maxH='280px' overflowY='auto'>
                      <Button
                        size='xs'
                        variant='ghost'
                        justifyContent='space-between'
                        onClick={cycleNoGroupFilter}
                      >
                        <Text fontSize='xs'>No group</Text>
                        {groupNoneFilter == null && <Icon as={FaEye} color='fg.muted' />}
                        {groupNoneFilter === 'include' && <Icon as={FaCheck} />}
                        {groupNoneFilter === 'exclude' && <Icon as={FaEyeSlash} color='fg.muted' />}
                      </Button>
                      {(groups || []).map(group => {
                        const mode = groupCycleMode(group._id)
                        return (
                          <Button
                            key={group._id}
                            size='xs'
                            variant='ghost'
                            justifyContent='space-between'
                            onClick={() => cycleGroupFilter(group._id)}
                          >
                            <ColouredTag size='sm' colour={group.colour}>{group.name}</ColouredTag>
                            {mode === 'neutral' && <Icon as={FaEye} color='fg.muted' />}
                            {mode === 'include' && <Icon as={FaCheck} />}
                            {mode === 'exclude' && <Icon as={FaEyeSlash} color='fg.muted' />}
                          </Button>
                        )
                      })}
                    </Stack>
                  </Box>
                </Stack>

                <Stack direction='row' justifyContent='end'>
                  <Button
                    size='xs'
                    variant='outline'
                    onClick={() =>
                      setParticipantsTableFilters({
                        scheduleFilters: [],
                        allocationFilters: [],
                        joinFilters: [],
                        showArchived: false,
                        groupIncludeIds: [],
                        groupExcludeIds: [],
                        groupNoneFilter: null,
                        selectedSiteId: null,
                        page: 1
                      })}
                  >
                    Clear filters
                  </Button>
                </Stack>
              </Stack>
            }
          />

          <SavedFilters />
        </Stack>

        <Stack direction='row' gap={2}>
          <SimplePopover
            trigger={<Button colorPalette='primary' variant='outline'>Using ePRO?</Button>}
            header='How to get your participants started'
            contentProps={{ w: 500 }}
            body={
              <ParticipantJoinInfo trial={trial} />
            }
          />
          {utils.canRegisterParticipants(user, trial, sites) &&
            <ParticipantCreator
              trigger={<Button colorPalette='primary'><Icon as={FaPlus} /> Register</Button>}
              trial={trial}
              groups={groups}
              onCreated={registeredParticipants => {
                const newParticipants = Object.assign([], participants)
                registeredParticipants.forEach(p => newParticipants.push(p))
                setParticipants(newParticipants)
                updateTrial(trial._id, { participantCount: (trial.participantCount ?? 0) + (registeredParticipants?.length || 0) })
              }}
            />}
        </Stack>
      </Stack>

      {filterChips.length > 0 && (
        <Wrap gap={2} alignItems='center'>
          {filterChips.map(c => {
            const handleClose = () => c.onRemove()
            return (
              <WrapItem key={c.key}>
                <Tag size='sm' variant='subtle' label={c.label} onClose={handleClose} />
              </WrapItem>
            )
          })}
        </Wrap>
      )}

      {loading && !total &&
        <Box textAlign='center'><Loading /></Box>}

      {loading ? <Progress value={null} size='sm' /> : <Box h={2} />}

      {!loading && !participants.length && (searchTerm || hasAnyFiltersApplied) &&
        <EmptyBox title='No participants match your search' description='Change your search terms and try again.' />}
      {!loading && !participants.length && !searchTerm && !hasAnyFiltersApplied &&
        <EmptyBox icon='people' title='Participants will appear here when they join or have been added to the trial' description='You can add participants individually or in bulk.' />}

      {participants && participants.length > 0 &&
        <SimpleTable
          shadow='md'
          rounded='md'
          size='sm'
          headers={[
            <Box key='actions'>
              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Menu
                  trigger={<Button size='xs' variant='outline'>{selectionParticipants?.length || 0} <Icon as={FaChevronDown} /></Button>}
                >
                  <MenuGroup title='Selection options'>
                    <MenuItem
                      value='all' onClick={() => {
                        const newSelected = Object.assign([], selectionParticipants)
                        filteredParticipants?.forEach(p => {
                          if (newSelected.indexOf(p._id) === -1) newSelected.push(p._id)
                        })
                        setSelectionParticipants(newSelected)
                      }}
                    >Select all on page
                    </MenuItem>
                    {selectionParticipants?.length > 0 &&
                      <MenuItem value='deselect' onClick={() => setSelectionParticipants([])}>De-select all</MenuItem>}
                  </MenuGroup>
                  {selectionParticipants?.length > 0 &&
                    <MenuGroup title='Options'>
                      <MenuItem value='sendPush' onClick={bulkNotifyParticipants}>Send push notification</MenuItem>
                      {utils.canTagParticipants(user, trial) && groups?.length > 0 &&
                        <>
                          <MenuSeparator />
                          {groups.map(g => (
                            <MenuItem key={g._id} value={`add-group-${g._id}`} onClick={() => bulkAssignGroup(g._id)}>
                              Add to {g.name}
                            </MenuItem>
                          ))}
                        </>}
                      {utils.canWriteTrial(user, trial) && sites?.length > 0 &&
                        <>
                          <MenuSeparator />
                          {sites.map(s => (
                            <MenuItem key={s._id} value={`add-site-${s._id}`} onClick={() => bulkAssignSite(s._id)}>
                              Add to site: {s.name}
                            </MenuItem>
                          ))}
                        </>}
                      {utils.canDeleteParticipants(user, trial) &&
                        <>
                          <MenuSeparator />
                          <MenuItem value='delete' onClick={bulkDeleteParticipants}>Delete participants</MenuItem>
                        </>}
                    </MenuGroup>}
                </Menu>
              </Stack>
            </Box>,
            <SortableTh
              key='id'
              sortKey='id'
              content='ID'
              sort={sort}
              sortOrder={sortOrder}
              setSort={value => setParticipantsTableFilters({ sort: value, page: 1 })}
              setSortOrder={value => setParticipantsTableFilters({ sortOrder: value, page: 1 })}
            />,
            <SortableTh
              key='ratioComplete'
              sortKey='ratioComplete'
              content='%'
              sort={sort}
              sortOrder={sortOrder}
              setSort={value => setParticipantsTableFilters({ sort: value, page: 1 })}
              setSortOrder={value => setParticipantsTableFilters({ sortOrder: value, page: 1 })}
            />,
            'Status',
            <SortableTh
              key='lastSeenAt'
              sortKey='lastSeenAt'
              content='Seen'
              sort={sort}
              sortOrder={sortOrder}
              setSort={value => setParticipantsTableFilters({ sort: value, page: 1 })}
              setSortOrder={value => setParticipantsTableFilters({ sortOrder: value, page: 1 })}
            />,
            'Sites',
            'Groups',
            <Box key='actions'>
              {utils.canReadTrial(user, trial) &&
                <Button size='2xs' variant='ghost' onClick={exportCSV} isLoading={exporting}>
                  <Icon as={FaDownload} />
                </Button>}
            </Box>
          ]}
          rows={filteredParticipants.map(p => [
            <Box key={p._id} w='50px' textAlign='center'>
              <Checkbox isChecked={selectionParticipants.indexOf(p._id) > -1} onChange={e => toggleSelected(p._id)} />
            </Box>,
            <Box key={p._id}>
              <Text as={Link} fontFamily='monospace' fontWeight='bold' to={`/trials/${trial._id}/participants/${p._id}`}>{p.id}</Text>
            </Box>,
            <Box key={p._id} textAlign='center'>
              {p.ratioComplete != null
                ? (<Tooltip label={`${parseInt(p.ratioComplete * 100)}%`}><Box display='inline-block'><CircularProgress value={p.ratioComplete * 100} size='xs' /></Box></Tooltip>)
                : null}
            </Box>,
            <Wrap gap={2} alignItems='center' key={p._id}>
              {p.isArchived && <WrapItem><Badge colorPalette='orange' variant='subtle' fontSize='xs'>Archived</Badge></WrapItem>}
              {p.isWithdrawn && <WrapItem><Badge colorPalette='red' variant='subtle' fontSize='xs'>Withdrawn</Badge></WrapItem>}
              {p.isUnallocated && <WrapItem><Badge colorPalette='yellow' variant='subtle' fontSize='xs'>Unallocated</Badge></WrapItem>}
              {p.pushAvailable &&
                <WrapItem><Badge colorPalette='purple' variant='subtle' fontSize='xs'>Push</Badge></WrapItem>}
              {p.lastSeenAt && <WrapItem><Badge colorPalette='green' variant='subtle' fontSize='xs'>Joined</Badge></WrapItem>}
              {p.connectedServices?.map(service =>
                <WrapItem key={service.name}>
                  <Badge colorPalette='blue' variant='subtle' fontSize='xs'>
                    {service.name}
                  </Badge>
                </WrapItem>
              )}
              {!p.isWithdrawn && p.stagesBehindSchedule?.length > 0 &&
                <WrapItem><Badge colorPalette='red' variant='subtle' fontSize='xs'>Behind</Badge></WrapItem>}
              {insightsFeature && p.latestInsights?.length > 0 &&
                <WrapItem>
                  <InsightBubble
                    insights={p.latestInsights}
                    trialId={trial._id}
                    participantId={p._id}
                    trialUsers={trialUsers}
                  />
                </WrapItem>}
            </Wrap>,
            <Box key={p._id}>
              {p.lastSeenAt && utils.formatDateTime(p.lastSeenAt)}<br />
              {p.lastLocation && <span><small>{p.lastLocation.city}, {p.lastLocation.country_name}</small></span>}
            </Box>,
            <Wrap gap={1} key={p._id} alignItems='center'>
              {p.siteObjects.map(s =>
                <WrapItem key={s._id}>
                  <Tag
                    size='sm' key={s._id} variant='subtle'
                    onClick={() => setParticipantsTableFilters({ selectedSiteId: s._id, page: 1 })}
                    label={s.name}
                    onClose={utils.canWriteTrial(user, trial) && sites?.length > 0 && (() => removeFromSite(p._id, s._id))}
                  />
                </WrapItem>)}
              {utils.canWriteTrial(user, trial) && sites?.length > 0 &&
                <WrapItem>
                  <Menu
                    trigger={<Button size='3xs' variant='ghost'><Icon as={FaPlus} /></Button>}
                  >
                    {sites.map(s =>
                      <MenuItem key={s._id} value={s._id} onClick={e => addToSite(p._id, s._id)}>{s.name}</MenuItem>
                    )}
                  </Menu>
                </WrapItem>}
            </Wrap>,
            <Wrap gap={1} key={p._id} alignItems='center'>
              {p.groupObjects.map(g =>
                <WrapItem key={g._id}>
                  <ColouredTag
                    size='sm'
                    colour={g.colour}
                    label={g.name}
                    onClick={() => setParticipantsTableFilters({
                      groupIncludeIds: [g._id],
                      groupExcludeIds: [],
                      groupNoneFilter: null,
                      page: 1
                    })}
                    onClose={utils.canTagParticipants(user, trial) && groups?.length > 0 && ((e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      if (window.confirm('Really remove this participant from this group?')) {
                        removeFromGroup(p._id, g._id)
                      }
                    })}
                  />
                </WrapItem>
              )}
              {utils.canTagParticipants(user, trial) && groups?.length > 0 &&
                <WrapItem>
                  <Menu
                    trigger={<Button size='3xs' variant='ghost'><Icon as={FaPlus} /></Button>}
                  >
                    {groups.map(g =>
                      <MenuItem key={g._id} value={g._id} onClick={e => addToGroup(p._id, g._id)}>{g.name}</MenuItem>
                    )}
                  </Menu>
                </WrapItem>}
            </Wrap>,
            <Box key={p._id}>
              <Menu
                trigger={<Button size='xs' variant='ghost'><Icon><FaChevronDown /></Icon></Button>}
              >
                <MenuItem value='view' as={Link} to={`/trials/${trial._id}/participants/${p._id}`}>View participant</MenuItem>
                {p.isUnallocated && utils.canAllocateParticipants(user, trial) &&
                  <MenuItem value='allocated' onClick={() => updateParticipant(p._id, { isUnallocated: false, allocatedReason: 'Participant update' })}>Mark as allocated</MenuItem>}
                {!p.isUnallocated && utils.canAllocateParticipants(user, trial) &&
                  <MenuItem value='unallocated' onClick={() => updateParticipant(p._id, { isUnallocated: true })}>Mark as unallocated</MenuItem>}
                {utils.canArchiveParticipants(user, trial) && !p.isArchived &&
                  <ConfirmButton
                    asSubMenu
                    header='Archive participant?'
                    body={
                      <Stack gap={2}>
                        <Text>
                          Archiving a participant will change how Trialflare treats them:
                        </Text>
                        <Box>
                          <Text fontSize='sm'>- They will be hidden by default from the participants table.</Text>
                          <Text fontSize='sm'>- No new data can be submitted.</Text>
                          <Text fontSize='sm'>- Their data will not be included in exports.</Text>
                        </Box>
                        <Text fontSize='sm' color='fg.muted'>
                          If you also want to stop automatic stage reminders, mark the participant as withdrawn as well.
                        </Text>
                      </Stack>
                    }
                    trigger={<Box>Archive participant</Box>}
                    onConfirm={() => updateParticipant(p._id, { isArchived: true })}
                  />}
                {utils.canArchiveParticipants(user, trial) && p.isArchived &&
                  <MenuItem value='unarchive' onClick={() => updateParticipant(p._id, { isArchived: false })}>Un-archive participant</MenuItem>}
                {utils.canWithdrawParticipants(user, trial) && !p.isWithdrawn &&
                  <ConfirmButton
                    asSubMenu
                    header='Mark participant as withdrawn?'
                    body={
                      <Stack gap={2}>
                        <Text>
                          Withdrawing a participant will change how Trialflare treats them:
                        </Text>
                        <Box>
                          <Text fontSize='sm'>- They will no longer receive automatic stage reminders.</Text>
                          <Text fontSize='sm'>- They will not be able to submit any further data.</Text>
                          <Text fontSize='sm'>- Trial staff can still update data on their behalf.</Text>
                        </Box>
                      </Stack>
                    }
                    trigger={<Box>Mark as withdrawn</Box>}
                    onConfirm={() => updateParticipant(p._id, { isWithdrawn: true })}
                  />}
                {utils.canWithdrawParticipants(user, trial) && p.isWithdrawn &&
                  <MenuItem value='unwithdraw' onClick={() => updateParticipant(p._id, { isWithdrawn: false })}>Unmark as withdrawn</MenuItem>}
                {utils.canDeleteParticipants(user, trial) &&
                  <ConfirmButton
                    asSubMenu
                    header='Really delete this participant?'
                    body='Associated data across all stages will also be removed.'
                    trigger={<Stack direction='row' alignItems='center' gap={2}><Icon><FaTrash /></Icon> Delete participant</Stack>}
                    onConfirm={e => deleteParticipant(p._id)}
                  />}
              </Menu>
            </Box>
          ])}
        />}

      <Stack direction='row' justifyContent='center'>
        {prevPageAvailable && (
          <Button
            size='xs'
            onClick={e => setParticipantsTableFilters({ page: page - 1 })}
          >
            <Icon as={FaArrowLeft} /> Previous page
          </Button>
        )}
        {nextPageAvailable && (
          <Button
            size='xs'
            onClick={e => setParticipantsTableFilters({ page: page + 1 })}
          >
            Next page <Icon as={FaArrowRight} />
          </Button>
        )}
      </Stack>
    </Stack>
  )
}

function InsightBubble ({ insights, trialId, participantId, trialUsers }) {
  const [open, setOpen] = useState(false)
  if (!insights || insights.length === 0) return null
  const hasHigh = insights.some(i => i.severity === 'high')
  const bubblePalette = hasHigh ? 'red' : 'orange'
  return (
    <>
      <Tooltip label={`${insights.length} insight${insights.length !== 1 ? 's' : ''}`}>
        <Badge
          colorPalette={bubblePalette}
          variant='solid'
          borderRadius='full'
          minW='20px'
          h='20px'
          display='flex'
          alignItems='center'
          justifyContent='center'
          cursor='pointer'
          fontSize='xs'
          onClick={e => { e.preventDefault(); e.stopPropagation(); setOpen(true) }}
        >
          {insights.length}
        </Badge>
      </Tooltip>

      <SimpleModal
        isOpen={open}
        onClose={() => setOpen(false)}
        header={<Text fontWeight='semibold'>Insights</Text>}
        body={
          <InsightCarousel
            insights={insights}
            trialId={trialId}
            participantId={participantId}
            trialUsers={trialUsers}
          />
        }
        footer={<Stack direction='row' justifyContent='end'><Button onClick={() => setOpen(false)}>Close</Button></Stack>}
      />
    </>
  )
}

function SortableTh ({ sortKey, content, sort, sortOrder, setSort, setSortOrder }) {
  const icon = sort === sortKey
    ? (
        sortOrder === 'asc' ? FaSortUp : FaSortDown
      )
    : FaSort
  return (
    <Box cursor='pointer'>
      <Box
        display='flex' alignItems='center' justifyContent='space-between'
        onClick={e => {
          if (sort === sortKey) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
          else setSort(sortKey)
        }}
      >
        <Text color={sortKey === sort ? 'fg.primary' : 'fg.default'}>{content}</Text>
        <Icon color='fg.muted' as={icon} />
      </Box>
    </Box>
  )
}
